import { useTranslations } from "use-intl";

import type { FeaturePageRenderModel } from "@garimpa/api/featurePages/types";
import { buildCdnRenderPath } from "@garimpa/lib/buildCdnRenderPath";

import { CollectionPanelBulkAction } from "./collection-panel-bulk-action";

type CollectionModel = NonNullable<FeaturePageRenderModel["collection"]>;

interface CollectionProgressSummary {
  missing: number;
  owned: number;
  percentage: number;
  total: number;
  wishlisted: number;
}

export function CollectionPanelHeader({
  bulkReleaseCount,
  canAddMissing,
  collection,
  displayedProgress,
  isAdding,
  isAuthenticated,
  onAddMissing,
}: {
  bulkReleaseCount: number;
  canAddMissing: boolean;
  collection: CollectionModel;
  displayedProgress: CollectionProgressSummary;
  isAdding: boolean;
  isAuthenticated: boolean;
  onAddMissing: () => void;
}) {
  const t = useTranslations("featureCollections");
  const origin = collection.definition.origin;
  const emblem = collection.definition.emblem;

  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-8">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t("eyebrow")}</p>
        <h2
          id="collection-members-title"
          className="mt-1 text-balance text-2xl font-black leading-tight sm:text-3xl"
        >
          {collection.presentation.label}
        </h2>
        <div className="mt-5 grid grid-cols-[6rem_minmax(0,1fr)] items-center gap-4">
          <div className="size-24 overflow-hidden rounded-2xl border border-border bg-muted/40 p-2 shadow-inner transition-transform duration-300 hover:-rotate-1 hover:scale-[1.02] motion-reduce:transition-none">
            <img
              src={buildCdnRenderPath(collection.emblemUrl, { width: 150, format: "webp" })}
              alt={collection.presentation.emblemLabel}
              width={150}
              height={150}
              className="size-full object-contain"
            />
          </div>
          <div className="min-w-0">
            {origin.kind === "external" ? (
              <a
                className="inline-block text-sm leading-relaxed text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                href={origin.url}
                rel="noreferrer"
                target="_blank"
              >
                {origin.publisher} · {origin.title}
              </a>
            ) : null}
            {emblem.source === "iconify" && emblem.licenseSpdx.startsWith("CC-BY") ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("emblemCredit", { author: emblem.author, license: emblem.licenseSpdx })}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="w-full rounded-xl border border-border bg-background/70 p-3 md:w-80 md:shrink-0">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">{t("progress")}</p>
            <p className="mt-0.5 text-xl font-black">
              {t("ownedCount", {
                owned: displayedProgress.owned,
                total: displayedProgress.total,
              })}
            </p>
          </div>
          <span className="text-lg font-black text-primary">{displayedProgress.percentage}%</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <div
            className="h-full rounded-full bg-primary transition-[width] motion-reduce:transition-none"
            style={{ width: `${displayedProgress.percentage}%` }}
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{t("wishlistedCount", { count: displayedProgress.wishlisted })}</span>
          <span>{t("missingCount", { count: displayedProgress.missing })}</span>
        </div>
        <CollectionPanelBulkAction
          bulkReleaseCount={bulkReleaseCount}
          canAddMissing={canAddMissing}
          isAdding={isAdding}
          isAuthenticated={isAuthenticated}
          onAddMissing={onAddMissing}
        />
      </div>
    </div>
  );
}
